import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, X, ChevronLeft, ChevronRight, Sparkles, Clock } from "lucide-react";
import { useSpaData } from "../context/SpaDataContext";
import { waLink, imgUrl } from "../lib/spa";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { LogoMark } from "./Logo";

const TOPBAR_DISMISS_KEY = "ml-promo-topbar-dismissed";
const POPUP_SEEN_KEY = "ml-promo-popup-seen";

export const bannerWaHref = (b) =>
  waLink(b.whatsapp_message || (b.title || b.message ? `Olá! Gostaria de saber mais sobre a promoção: ${b.title || b.message}` : "Olá! Gostaria de saber mais sobre a vossa promoção."));

/* ---------- Cliques (anónimos) ---------- */
export const trackBannerClick = (b, placement) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/${b.id}/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ placement }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* silencioso */
  }
};

/* ---------- Contador regressivo (fim do dia em Lisboa) ---------- */
const lisbonOffsetMs = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Lisbon", hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

export const promoEndTime = (endsAt) => {
  if (!endsAt) return null;
  const [y, m, d] = endsAt.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d, 23, 59, 59);
  return guess - lisbonOffsetMs(new Date(guess));
};

const useCountdown = (endsAt) => {
  const end = useMemo(() => promoEndTime(endsAt), [endsAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!end) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [end]);
  if (!end) return null;
  const left = Math.max(0, end - now);
  const s = Math.floor(left / 1000);
  return { left, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

const pad = (n) => String(n).padStart(2, "0");

export const PromoCountdown = ({ endsAt, variant = "boxes", testId = "promo-countdown" }) => {
  const c = useCountdown(endsAt);
  if (!c || c.left <= 0) return null;
  if (variant === "inline") {
    return (
      <span data-testid={testId} className="inline-flex shrink-0 items-center gap-1 font-semibold tabular-nums">
        <Clock className="h-3 w-3" strokeWidth={2} />
        {c.d > 0 ? `${c.d}d ` : ""}
        {pad(c.h)}:{pad(c.m)}:{pad(c.s)}
      </span>
    );
  }
  const units = [
    { v: c.d, l: c.d === 1 ? "dia" : "dias" },
    { v: c.h, l: "horas" },
    { v: c.m, l: "min" },
    { v: c.s, l: "seg" },
  ];
  return (
    <div data-testid={testId} className="flex flex-col items-center gap-2">
      <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.35em] text-gold">
        <Clock className="h-3.5 w-3.5" strokeWidth={1.5} /> A promoção termina em
      </p>
      <div className="flex gap-2 sm:gap-3">
        {units.map((u) => (
          <div
            key={u.l}
            className="flex min-w-[58px] flex-col items-center rounded-xl border border-gold/30 bg-black/60 px-2 py-2 text-white sm:min-w-[68px]"
          >
            <span className="font-display text-2xl tabular-nums leading-none text-gold sm:text-3xl">{pad(u.v)}</span>
            <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-white/55">{u.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const useBannersFor = (placement) => {
  const { banners = [] } = useSpaData();
  return useMemo(() => banners.filter((b) => (b.placements || []).includes(placement)), [banners, placement]);
};

const useRotation = (count, ms = 6000) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (idx >= count) setIdx(0);
  }, [count, idx]);
  useEffect(() => {
    if (count < 2) return undefined;
    const id = setTimeout(() => setIdx((i) => (i + 1) % count), ms);
    return () => clearTimeout(id);
  }, [count, ms, idx]);
  return [Math.min(idx, Math.max(count - 1, 0)), setIdx];
};

const SaberMais = ({ banner, testId, placement, className = "" }) => (
  <a
    href={bannerWaHref(banner)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    onClick={() => trackBannerClick(banner, placement)}
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-gold font-semibold uppercase text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] ${className}`}
  >
    <WhatsAppIcon className="h-4 w-4" />
    {banner.button_label || "Saber mais"}
  </a>
);

/* ---------------- Barra fina no topo (todas as páginas) ---------------- */
export const usePromoTopBarVisible = () => {
  const items = useBannersFor("topbar");
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(TOPBAR_DISMISS_KEY) || "");
  const key = items.map((b) => b.id).join(",");
  const visible = items.length > 0 && dismissed !== key;
  const dismiss = () => {
    sessionStorage.setItem(TOPBAR_DISMISS_KEY, key);
    setDismissed(key);
  };
  return { items, visible, dismiss };
};

export const PROMO_TOPBAR_H = 40;

export const PromoTopBar = ({ items, onDismiss }) => {
  const [idx] = useRotation(items.length, 5000);
  const b = items[idx];
  if (!b) return null;
  const text = [b.title, b.message].filter(Boolean).join(" — ") || "Promoção especial";
  return (
    <div
      data-testid="promo-topbar"
      style={{ height: PROMO_TOPBAR_H }}
      className="relative flex items-center justify-center overflow-hidden bg-gradient-to-r from-gold-dark via-gold to-gold-dark px-10 text-black"
    >
      <AnimatePresence mode="wait">
        <motion.a
          key={b.id}
          href={bannerWaHref(b)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="promo-topbar-link"
          onClick={() => trackBannerClick(b, "topbar")}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="flex min-w-0 items-center gap-2 text-[11px] font-medium tracking-wide sm:text-xs"
        >
          <Gift className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
          <span className="hidden truncate sm:inline">{text}</span>
          <span className="line-clamp-2 text-[10.5px] leading-tight sm:hidden">{b.message || text}</span>
          {b.ends_at && (
            <span className="shrink-0 rounded-full bg-black/10 px-2 py-0.5 text-[10px] sm:text-[11px]">
              <PromoCountdown endsAt={b.ends_at} variant="inline" testId="promo-topbar-countdown" />
            </span>
          )}
          <span className="hidden shrink-0 rounded-full bg-black/85 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-gold sm:inline-block">
            {b.button_label || "Saber mais"}
          </span>
        </motion.a>
      </AnimatePresence>
      <button
        type="button"
        aria-label="Fechar"
        data-testid="promo-topbar-close"
        onClick={onDismiss}
        className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-black/70 transition hover:bg-black/10 hover:text-black"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

/* ---------------- Faixa grande na página inicial ---------------- */
export const isImageOnly = (b) =>
  !!(b.image || b.image_mobile) && (b.show_text === false || !(b.title || b.message));

const altOf = (b) => [b.title, b.message].filter(Boolean).join(" — ") || "Promoção Magnolia Lunar";

const HomeSlide = ({ b }) => {
  if (isImageOnly(b)) {
    const desktop = b.image || b.image_mobile;
    const mobile = b.image_mobile || b.image;
    return (
      <div className="flex flex-col items-center gap-7" data-testid="promo-home-image-only">
        <a
          href={bannerWaHref(b)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackBannerClick(b, "home")}
          className="group mx-auto block w-full max-w-4xl overflow-hidden rounded-3xl border border-gold/25 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.35)]"
        >
          <picture>
            <source media="(max-width: 639px)" srcSet={imgUrl(mobile)} />
            <img
              src={imgUrl(desktop)}
              alt={altOf(b)}
              data-testid="promo-home-image"
              className="mx-auto block h-auto w-full transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </picture>
        </a>
        {b.ends_at && <PromoCountdown endsAt={b.ends_at} testId="promo-home-countdown" />}
        <SaberMais banner={b} placement="home" testId="promo-home-cta" className="w-full px-12 py-4 text-xs tracking-[0.25em] sm:w-auto" />
      </div>
    );
  }
  return (
    <div className="grid overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-br from-gold/[0.12] via-background to-background md:grid-cols-2">
      <div className="relative min-h-[260px] overflow-hidden md:min-h-[420px]">
        {b.image ? (
          <img src={imgUrl(b.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_30%_30%,rgba(212,175,55,0.28),transparent_60%),linear-gradient(135deg,#0d0d0d,#1a1407)]">
            <div className="text-center">
              <LogoMark className="mx-auto h-20 w-20 opacity-90" />
              <Gift className="mx-auto mt-6 h-10 w-10 text-gold" strokeWidth={1.2} />
              <p className="mt-4 font-display text-4xl font-light italic text-gold sm:text-5xl">Oferta especial</p>
              <p className="mt-3 text-[10px] uppercase tracking-[0.45em] text-white/60">Magnolia Lunar Spa</p>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/10" />
      </div>
      <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-16">
        <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.45em] text-gold">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Promoção
        </p>
        {b.title && (
          <h2 className="mt-4 font-display text-4xl font-light leading-tight text-foreground sm:text-5xl">{b.title}</h2>
        )}
        {b.message && (
          <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/80 sm:text-lg">{b.message}</p>
        )}
        {b.ends_at && (
          <div className="mt-6 flex flex-col items-start gap-2">
            <PromoCountdown endsAt={b.ends_at} testId="promo-home-countdown" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-foreground/55">
              Válido até {new Date(`${b.ends_at}T00:00:00`).toLocaleDateString("pt-PT")}
            </p>
          </div>
        )}
        <div className="mt-8">
          <SaberMais banner={b} placement="home" testId="promo-home-cta" className="w-full px-10 py-4 text-xs tracking-[0.25em] sm:w-auto" />
        </div>
      </div>
    </div>
  );
};

export const PromoHomeBanner = () => {
  const items = useBannersFor("home");
  const [idx, setIdx] = useRotation(items.length, 7000);
  if (!items.length) return null;
  const b = items[idx];
  return (
    <section data-testid="promo-home-banner" className="mx-auto max-w-7xl px-5 pt-20 sm:px-8 sm:pt-28">
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={b.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45 }}
          >
            <HomeSlide b={b} />
          </motion.div>
        </AnimatePresence>
        {items.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              type="button"
              aria-label="Anterior"
              onClick={() => setIdx((idx - 1 + items.length) % items.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/15 text-foreground/70 transition hover:border-gold hover:text-gold"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex gap-2">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  aria-label={`Promoção ${i + 1}`}
                  data-testid={`promo-home-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-gold" : "w-3 bg-foreground/25"}`}
                />
              ))}
            </div>
            <button
              type="button"
              aria-label="Seguinte"
              onClick={() => setIdx((idx + 1) % items.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/15 text-foreground/70 transition hover:border-gold hover:text-gold"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Janela pop-up ao entrar ---------------- */
export const PromoPopup = () => {
  const items = useBannersFor("popup");
  const b = items[0];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!b) return undefined;
    if (sessionStorage.getItem(POPUP_SEEN_KEY) === b.id) return undefined;
    let timer;
    const tryOpen = () => {
      if (localStorage.getItem("ml-age-ok") === "1") {
        timer = setTimeout(() => setOpen(true), 1200);
        return true;
      }
      return false;
    };
    if (tryOpen()) return () => clearTimeout(timer);
    const iv = setInterval(() => {
      if (tryOpen()) clearInterval(iv);
    }, 500);
    return () => {
      clearInterval(iv);
      clearTimeout(timer);
    };
  }, [b]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (b) sessionStorage.setItem(POPUP_SEEN_KEY, b.id);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && b && (
        <motion.div
          data-testid="promo-popup"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 px-5 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-gold/30 bg-[#101010] text-center text-white shadow-2xl shadow-black/60"
          >
            <button
              type="button"
              aria-label="Fechar"
              data-testid="promo-popup-close"
              onClick={close}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white/80 transition hover:bg-black hover:text-gold"
            >
              <X className="h-4 w-4" />
            </button>
            {(b.image_mobile || b.image) && (
              <a
                href={bannerWaHref(b)}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={() => trackBannerClick(b, "popup")}
              >
                <img
                  src={imgUrl(b.image_mobile || b.image)}
                  alt={altOf(b)}
                  data-testid="promo-popup-image"
                  className="max-h-[60vh] w-full bg-black object-contain"
                />
              </a>
            )}
            <div className={isImageOnly(b) ? "p-6 sm:p-7" : "p-8 sm:p-10"}>
              {!isImageOnly(b) && (
                <>
                  {!b.image && <LogoMark className="mx-auto mb-4 h-12 w-12" />}
                  <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.45em] text-gold">
                    <Gift className="h-3.5 w-3.5" strokeWidth={1.5} /> Promoção
                  </p>
                  {b.title && <h2 className="mt-3 font-display text-3xl font-medium">{b.title}</h2>}
                  {b.message && <p className="mt-4 text-sm leading-relaxed text-white/75">{b.message}</p>}
                </>
              )}
              {b.ends_at && (
                <div className={isImageOnly(b) ? "mb-5" : "mt-6"}>
                  <PromoCountdown endsAt={b.ends_at} testId="promo-popup-countdown" />
                </div>
              )}
              <SaberMais
                banner={b}
                placement="popup"
                testId="promo-popup-cta"
                className={`${isImageOnly(b) ? "" : "mt-8 "}w-full px-8 py-4 text-xs tracking-[0.2em]`}
              />
              <button
                type="button"
                onClick={close}
                className="mt-4 text-xs text-white/45 underline-offset-4 transition hover:text-white hover:underline"
              >
                Agora não
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
